import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  IconArrow, IconUsers, IconTool, IconClock, IconShield, IconCheckShield,
  IconCheckCircle, IconCheck, IconClipboard, IconMail, IconClock2,
  IconUpload, IconFile, IconClipboardList, IconFacebook, IconInstagram,
} from "./components/icons";
import { PrivacyPage, TermsPage } from "./LegalPages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- ScrollReveal wrapper ---------- */
const Rev = ({ children, className = "", style, ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Ensure element becomes visible even if observer misses
    const fallback = setTimeout(() => el.classList.add("in"), 300);
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { el.classList.add("in"); obs.unobserve(el); clearTimeout(fallback); }
      }),
      { threshold: 0.05, rootMargin: "0px 0px -10% 0px" }
    );
    obs.observe(el);
    return () => { obs.disconnect(); clearTimeout(fallback); };
  }, []);
  return (
    <div ref={ref} className={`rev ${className}`} style={style} {...rest}>
      {children}
    </div>
  );
};

/* ---------- NAV ---------- */
const Nav = ({ page, goTo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.pageYOffset > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    ["home", "Home"], ["about", "About"], ["builders", "Employers"],
    ["workers", "Talent"], ["services", "Services"], ["contact", "Contact"],
  ];
  const close = () => setMobOpen(false);
  return (
    <>
      <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-inner">
          <div className="logo" onClick={() => goTo("home")} data-testid="nav-logo">
            <div className="logo-mark"></div>
            <div>
              <span className="logo-text">Delta Talent</span>
              <span className="logo-sub">Workforce Solutions</span>
            </div>
          </div>
          <div className="nav-links">
            {links.map(([id, label]) => (
              <button key={id} className="nav-link" onClick={() => goTo(id)} data-testid={`nav-link-${id}`}>{label}</button>
            ))}
            <button className="btn btn-gold nav-cta" onClick={() => goTo("home", "request")} data-testid="nav-request-talent">Request Talent</button>
          </div>
          <div className="burger" onClick={() => setMobOpen((v) => !v)} data-testid="nav-burger">
            <span></span><span></span><span></span>
          </div>
        </div>
      </nav>
      <div className={`mob-menu ${mobOpen ? "open" : ""}`}>
        {links.map(([id, label]) => (
          <button key={id} className="nav-link" onClick={() => { goTo(id); close(); }}>{label}</button>
        ))}
        <button className="btn btn-gold" onClick={() => { goTo("home", "request"); close(); }}>Request Talent</button>
      </div>
    </>
  );
};

/* ---------- HERO (Home) ---------- */
const HomeHero = ({ goTo }) => (
  <section className="hero">
    <div className="hero-bg"></div>
    <div className="hero-overlay"></div>
    <div className="wrap">
      <div className="hero-content">
        <span className="hero-eyebrow">Sydney's Construction Workforce Partner</span>
        <h1>Reliable People.<br/><em>Safer Sites.</em><br/>Better Outcomes.</h1>
        <p className="hero-lead">Delta Talent connects Australia's leading builders and contractors with vetted, capable construction workers — ready when you need them.</p>
        <div className="hero-actions">
          <button className="btn btn-gold" onClick={() => goTo("home", "request")} data-testid="hero-hire-talent">
            <IconUsers /> Hire Talent
          </button>
          <button className="btn btn-outline" onClick={() => goTo("workers")} data-testid="hero-find-work">
            Find Work <IconArrow size={16} />
          </button>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- REQUEST TALENT FORM ---------- */
const RequestTalentForm = () => {
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    company_name: "", contact_person: "", phone: "", email: "",
    num_workers: "", trade_type: "", start_date: "", duration: "", project_details: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.company_name || !form.contact_person || !form.phone || !form.email || !form.num_workers || !form.trade_type) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/talent-requests`, { ...form, num_workers: parseInt(form.num_workers) });
      setSuccess(true);
      toast.success("Request received!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong. Try again.");
    } finally { setLoading(false); }
  };
  if (success) {
    return (
      <div className="form-success" data-testid="req-success">
        <IconCheckCircle />
        <h4>Request Received!</h4>
        <p>Our team will review your request and be in touch to confirm your workers.</p>
      </div>
    );
  }
  return (
    <div data-testid="request-form">
      <div className="form-row">
        <div className="fg"><label>Company Name *</label><input type="text" placeholder="e.g. ABC Builders" value={form.company_name} onChange={upd("company_name")} data-testid="req-company"/></div>
        <div className="fg"><label>Contact Person *</label><input type="text" placeholder="Your full name" value={form.contact_person} onChange={upd("contact_person")} data-testid="req-contact"/></div>
      </div>
      <div className="form-row">
        <div className="fg"><label>Phone Number *</label><input type="tel" placeholder="04XX XXX XXX" value={form.phone} onChange={upd("phone")} data-testid="req-phone"/></div>
        <div className="fg"><label>Email Address *</label><input type="email" placeholder="you@company.com.au" value={form.email} onChange={upd("email")} data-testid="req-email"/></div>
      </div>
      <div className="form-row">
        <div className="fg"><label>Number of Workers *</label><input type="number" placeholder="e.g. 5" min="1" value={form.num_workers} onChange={upd("num_workers")} data-testid="req-workers"/></div>
        <div className="fg"><label>Trade Type *</label>
          <select value={form.trade_type} onChange={upd("trade_type")} data-testid="req-trade">
            <option value="" disabled>Select trade type</option>
            {["General Labourer","Carpenter","Concreter / Steel Fixer","Formworker","Machine Operator","Scaffolder","Site Supervisor","Electrician","Plumber","Other"].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="fg"><label>Start Date *</label><input type="date" min={today} value={form.start_date} onChange={upd("start_date")} data-testid="req-startdate"/></div>
        <div className="fg"><label>Duration</label>
          <select value={form.duration} onChange={upd("duration")} data-testid="req-duration">
            <option value="" disabled>Estimated duration</option>
            {["1–2 weeks","2–4 weeks","1–3 months","3–6 months","6+ months","Ongoing"].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="fg"><label>Project Details</label><textarea placeholder="Tell us about your project, site location, and any specific requirements..." value={form.project_details} onChange={upd("project_details")} data-testid="req-details"/></div>
      <button type="button" disabled={loading} className="btn btn-gold" style={{width:"100%",justifyContent:"center",marginTop:6, opacity: loading ? .7 : 1}} onClick={submit} data-testid="req-submit">
        {loading ? "Submitting..." : "Submit Request"} <IconArrow />
      </button>
    </div>
  );
};

/* ---------- CONTACT (message) FORM ---------- */
const MessageForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in all required fields."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/messages`, form);
      setSuccess(true);
      toast.success("Message sent!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong.");
    } finally { setLoading(false); }
  };
  if (success) {
    return (
      <div className="form-success" data-testid="msg-success">
        <IconCheckCircle />
        <h4>Message Sent!</h4>
        <p>Thanks for reaching out. We'll be in touch as soon as we can.</p>
      </div>
    );
  }
  return (
    <div data-testid="message-form">
      <div className="fg"><label>Your Name *</label><input type="text" placeholder="Full name" value={form.name} onChange={upd("name")} data-testid="msg-name"/></div>
      <div className="fg"><label>Email Address *</label><input type="email" placeholder="you@email.com" value={form.email} onChange={upd("email")} data-testid="msg-email"/></div>
      <div className="fg"><label>Phone Number</label><input type="tel" placeholder="04XX XXX XXX" value={form.phone} onChange={upd("phone")} data-testid="msg-phone"/></div>
      <div className="fg"><label>I am a...</label>
        <select value={form.role} onChange={upd("role")} data-testid="msg-role">
          <option value="" disabled>Select one</option>
          <option>Builder / contractor looking for workers</option>
          <option>Worker / tradesperson looking for work</option>
          <option>Business with a general enquiry</option>
          <option>Other</option>
        </select>
      </div>
      <div className="fg"><label>Message *</label><textarea placeholder="Tell us how we can help..." value={form.message} onChange={upd("message")} data-testid="msg-text"/></div>
      <button type="button" disabled={loading} className="btn btn-gold" style={{width:"100%",justifyContent:"center",marginTop:6, opacity: loading ? .7 : 1}} onClick={submit} data-testid="msg-submit">
        {loading ? "Sending..." : "Send Message"} <IconArrow />
      </button>
    </div>
  );
};

/* ---------- CV FORM ---------- */
const CVForm = () => {
  const [form, setForm] = useState({ full_name:"", email:"", phone:"", trade:"", experience:"", licences:"", availability:"", notes:"" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setFile(f);
  };
  const submit = async () => {
    if (!form.full_name || !form.email || !form.phone || !form.trade) { toast.error("Please fill in all required fields."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      if (file) fd.append("cv_file", file);
      await axios.post(`${API}/cv-submissions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSuccess(true);
      toast.success("CV received!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong.");
    } finally { setLoading(false); }
  };
  if (success) {
    return (
      <div className="form-success" data-testid="cv-success">
        <IconCheckCircle />
        <h4>CV Received!</h4>
        <p>Thanks for sending through your details. We'll be in touch when we have a role that suits you.</p>
      </div>
    );
  }
  return (
    <div data-testid="cv-form">
      <div className="fg"><label>Full Name *</label><input type="text" placeholder="Your full name" value={form.full_name} onChange={upd("full_name")} data-testid="cv-fullname"/></div>
      <div className="fg"><label>Email Address *</label><input type="email" placeholder="you@email.com" value={form.email} onChange={upd("email")} data-testid="cv-email"/></div>
      <div className="fg"><label>Phone Number *</label><input type="tel" placeholder="04XX XXX XXX" value={form.phone} onChange={upd("phone")} data-testid="cv-phone"/></div>
      <div className="fg"><label>Your Trade / Role *</label>
        <select value={form.trade} onChange={upd("trade")} data-testid="cv-trade">
          <option value="" disabled>What's your trade or skillset?</option>
          {["General Labourer","Carpenter","Concreter / Steel Fixer","Formworker","Machine Operator","Scaffolder","Site Supervisor","Electrician","Plumber","Painter","Tiler","Forklift Operator","Truck Driver","Warehouse / Logistics","Other"].map(t => <option key={t}>{t}</option>)}
        </select>
      </div>
      <div className="fg"><label>Years of Experience</label>
        <select value={form.experience} onChange={upd("experience")} data-testid="cv-experience">
          <option value="" disabled>How long have you been in the trade?</option>
          {["Less than 1 year","1–2 years","3–5 years","5–10 years","10+ years"].map(t => <option key={t}>{t}</option>)}
        </select>
      </div>
      <div className="fg"><label>Licences &amp; Tickets</label><input type="text" placeholder="e.g. White Card, HR Licence, EWP, Forklift..." value={form.licences} onChange={upd("licences")} data-testid="cv-licences"/></div>
      <div className="fg"><label>Availability</label>
        <select value={form.availability} onChange={upd("availability")} data-testid="cv-availability">
          <option value="" disabled>When can you start?</option>
          {["Immediately","Within 1 week","Within 2 weeks","1 month+"].map(t => <option key={t}>{t}</option>)}
        </select>
      </div>
      <div className="fg">
        <label>Attach Your CV</label>
        <div style={{border:"2px dashed var(--grey-light)",borderRadius:"var(--r)",padding:24,textAlign:"center",cursor:"pointer",transition:"border-color var(--tr)"}}
             onClick={() => fileInputRef.current?.click()} data-testid="cv-file-drop">
          <IconUpload />
          <p style={{fontSize:14,color:"var(--grey)",margin:0}}>
            {file ? <strong style={{color:"var(--navy)"}}>{file.name}</strong> : <>Click to upload your CV<br/><span style={{fontSize:12}}>PDF, DOC or DOCX — max 5MB</span></>}
          </p>
          <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{display:"none"}} onChange={onFileChange} data-testid="cv-file-input"/>
        </div>
      </div>
      <div className="fg"><label>Anything else we should know?</label><textarea placeholder="Preferred locations, work types, anything relevant..." style={{minHeight:70}} value={form.notes} onChange={upd("notes")} data-testid="cv-notes"/></div>
      <button type="button" disabled={loading} className="btn btn-navy" style={{width:"100%",justifyContent:"center",marginTop:6, opacity: loading ? .7 : 1}} onClick={submit} data-testid="cv-submit">
        {loading ? "Submitting..." : "Submit CV"} <IconArrow />
      </button>
    </div>
  );
};

/* ---------- TESTIMONIALS SLIDER ---------- */
const TESTIMONIALS = [
  ["MR","Michael R.","Project Manager — Commercial Builder, Sydney","Delta Talent is the only labour hire company we use for our Sydney CBD projects. The quality of workers and responsiveness of their team is genuinely better than anyone else we've tried."],
  ["SC","Sandra C.","Site Director — Residential Developer, Western Sydney","We had a critical schedule issue and needed 15 concreters fast. Delta Talent delivered 17 — all experienced, all compliant. That's the kind of partner you want."],
  ["JT","Jamie T.","Steel Fixer — Delta Talent Worker, 3 Years","What I appreciate most is that Delta Talent treats you like a professional. Consistent work, fair pay and they've helped me develop my career over three years."],
  ["P","Peyton","Director — Sala Construction","Used Delta Talent on a couple of occasions now and been happy with the lads supplied."],
  ["D","Dimitri","Owner — NIE Electrical","We needed workers at short notice and Delta Talent sorted. The whole process was straightforward."],
  ["J","Jonathan","Director — Ryser","Mike was really easy to work with, highly recommend."],
];

const TestimonialSlider = () => {
  const trackRef = useRef(null);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const computePages = () => {
      const cards = el.querySelectorAll(".test-card");
      if (cards.length === 0) return;
      const perView = Math.max(1, Math.round(el.clientWidth / (cards[0].offsetWidth + 22)));
      setPageCount(Math.max(1, Math.ceil(cards.length / perView)));
    };
    const onScroll = () => {
      const cards = el.querySelectorAll(".test-card");
      if (cards.length === 0) return;
      const perView = Math.max(1, Math.round(el.clientWidth / (cards[0].offsetWidth + 22)));
      const idx = Math.round(el.scrollLeft / ((cards[0].offsetWidth + 22) * perView));
      setPage(idx);
    };
    // Defer initial measurement until layout settles (fonts, images etc.)
    const raf = requestAnimationFrame(computePages);
    const ro = new ResizeObserver(computePages);
    ro.observe(el);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener("scroll", onScroll);
    };
  }, []);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector(".test-card");
    if (!card) return;
    const perView = Math.max(1, Math.round(el.clientWidth / (card.offsetWidth + 22)));
    el.scrollBy({ left: dir * (card.offsetWidth + 22) * perView, behavior: "smooth" });
  };

  const goTo = (targetPage) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector(".test-card");
    if (!card) return;
    const perView = Math.max(1, Math.round(el.clientWidth / (card.offsetWidth + 22)));
    el.scrollTo({ left: targetPage * (card.offsetWidth + 22) * perView, behavior: "smooth" });
  };

  return (
    <div className="test-slider" data-testid="testimonial-slider">
      <button
        className="test-nav test-nav-prev"
        onClick={() => scrollBy(-1)}
        aria-label="Previous testimonials"
        disabled={page === 0}
        data-testid="testimonial-prev"
      >
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div className="test-track" ref={trackRef}>
        {TESTIMONIALS.map(([av, name, role, quote]) => (
          <div key={name} className="test-card">
            <div className="stars">★★★★★</div>
            <blockquote>"{quote}"</blockquote>
            <div className="test-author">
              <div className="test-avatar">{av}</div>
              <div><span className="test-name">{name}</span><span className="test-role">{role}</span></div>
            </div>
          </div>
        ))}
      </div>
      <button
        className="test-nav test-nav-next"
        onClick={() => scrollBy(1)}
        aria-label="Next testimonials"
        disabled={page >= pageCount - 1}
        data-testid="testimonial-next"
      >
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6"/></svg>
      </button>
      <div className="test-dots" role="tablist">
        {Array.from({ length: pageCount }).map((_, i) => (
          <button
            key={`dot-${i}`}
            className={`test-dot ${i === page ? "active" : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Go to page ${i + 1}`}
            data-testid={`testimonial-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
};

/* ---------- PAGES ---------- */
const HomePage = ({ goTo }) => (
  <div id="home">
    <HomeHero goTo={goTo} />
    <section className="sec">
      <div className="wrap">
        <div className="about-split">
          <Rev className="img-block"><img src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=900&q=80" alt="Construction workers collaborating on a Sydney site" loading="lazy"/></Rev>
          <Rev>
            <span className="eyebrow">Who We Are</span>
            <h2 style={{color:"var(--navy)"}}>Built on the Values the Industry Runs On</h2>
            <p className="lead" style={{margin:"18px 0 22px"}}>Delta Talent was founded in Sydney with one straightforward ambition: to be the labour hire company that builders actually trust.</p>
            <p>We know that a crew turning up late, unqualified, or unprepared doesn't just delay a project — it damages your reputation. That's why every worker we supply is vetted, inducted, and genuinely ready to contribute from day one.</p>
            <div style={{marginTop:32,display:"flex",gap:14,flexWrap:"wrap"}}>
              <button className="btn btn-navy" onClick={() => goTo("about")}>Our Story</button>
              <button className="btn btn-outline-dark" onClick={() => goTo("builders")}>How We Work</button>
            </div>
          </Rev>
        </div>
      </div>
    </section>

    <section className="sec sec-ofw">
      <div className="wrap">
        <Rev style={{maxWidth:620,marginBottom:56}}>
          <span className="eyebrow">What We Supply</span>
          <h2 style={{color:"var(--navy)"}}>The Right Trades. The Right Time.</h2>
          <p className="lead" style={{marginTop:14}}>From labourers to supervisors, we supply qualified construction professionals across every stage of your project.</p>
        </Rev>
        <div className="svc-grid">
          {[
            { icon: <IconUsers />, title: "General Labourers", desc: "Reliable, safety-conscious labourers ready to work hard from day one.", list: ["White Card certified","PPE compliant","Short and long-term","NSW-wide"] },
            { icon: <IconTool />, title: "Skilled Tradespeople", desc: "Carpenters, concreters, steel fixers, formworkers and more.", list: ["Trade-qualified","Reference-checked","Industry-experienced","Available nationally"] },
            { icon: <IconClock />, title: "Machine Operators", desc: "HRWL-licensed operators for excavators, forklifts, EWP and heavy plant.", list: ["Licence-verified","Plant-specific experience","Safety-record checked","Rapid deployment"] },
            { icon: <IconUsers />, title: "Site Supervisors", desc: "Experienced supervisors who manage crews, ensure compliance and keep projects on schedule.", list: ["WHS trained","Multi-discipline oversight","Tier 1 experience","Strong communicators"] },
            { icon: <IconClipboard />, title: "Workforce Planning", desc: "For larger projects, we plan, scale and manage labour across the full project lifecycle.", list: ["Workforce modelling","Surge capacity","Multi-site coordination","Account management"] },
            { icon: <IconShield />, title: "Compliance Management", desc: "We handle licensing, insurance, payroll and WHS compliance so you stay protected.", list: ["Employer of record","Workers' comp covered","Licence audits","Incident reporting"] },
          ].map((s) => (
            <Rev key={s.title} className="svc-card">
              <div className="svc-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
              <ul className="svc-list">{s.list.map(x => <li key={x}>{x}</li>)}</ul>
            </Rev>
          ))}
        </div>
      </div>
    </section>

    <div className="why-split">
      <div className="why-img">
        <img src="https://images.unsplash.com/photo-1590496793929-36417d3117de?w=900&q=80" alt="Engineer reviewing building plans" loading="lazy"/>
        <div className="why-img-ov"></div>
      </div>
      <div className="why-content">
        <span className="eyebrow">Why Delta Talent</span>
        <h2>We're Not a Recruiters' Database. We're Your Workforce Partner.</h2>
        <div className="why-list">
          {[
            ["01","Pre-vetted. Every Time.","Every worker goes through thorough screening — references, licence checks, site-readiness — before we send them to you."],
            ["02","Fast Turnaround. No Excuses.","When your project schedule changes, we move quickly. Our team works hard to get the right people confirmed as fast as possible."],
            ["03","Full Compliance. Your Protection.","We manage all payroll, WHS obligations and licensing — reducing your liability and giving you peace of mind."],
            ["04","A Relationship, Not a Transaction.","We take time to understand your projects, standards and culture — so we match well, not just quickly."],
          ].map(([n,t,d]) => (
            <Rev key={n} className="why-item"><span className="why-num">{n}</span><div><h4>{t}</h4><p>{d}</p></div></Rev>
          ))}
        </div>
      </div>
    </div>

    <section className="sec">
      <div className="wrap">
        <Rev style={{textAlign:"center",maxWidth:540,margin:"0 auto"}}>
          <span className="eyebrow">Project Sectors</span>
          <h2 style={{color:"var(--navy)"}}>We Work Across Every Sector</h2>
        </Rev>
        <div className="ind-grid">
          {[
            ["https://images.unsplash.com/photo-1486325212027-8081e485255e?w=600&q=80","Commercial Construction","Office towers, retail & mixed-use"],
            ["https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=600&q=80","Residential Developments","Apartments, townhouses & estates"],
            ["https://images.unsplash.com/photo-1503708928676-1cb796a0891e?w=600&q=80","Civil Infrastructure","Roads, bridges & utilities"],
            ["https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80","Fit-Outs & Refurbishments","Interior & commercial upgrades"],
          ].map(([src,t,s]) => (
            <Rev key={t} className="ind-card">
              <img src={src} alt={t} loading="lazy"/>
              <div className="ind-ov"></div>
              <div className="ind-label"><h4>{t}</h4><span>{s}</span></div>
            </Rev>
          ))}
        </div>
      </div>
    </section>

    <section className="sec sec-navy">
      <div className="wrap">
        <Rev style={{maxWidth:560}}>
          <span className="eyebrow">Safety First. Always.</span>
          <h2>Safety Is Not a Box to Tick. It's How We Operate.</h2>
          <p className="lead" style={{marginTop:14}}>Every worker Delta Talent supplies is inducted, equipped and safety-aware before they step foot on your site.</p>
        </Rev>
        <div className="saf-grid">
          {[
            [<IconShield/>,"WHS Commitment","Full compliance with NSW Work Health & Safety legislation across every placement."],
            [<IconClock/>,"Site-Specific Inductions","Workers receive site-specific inductions before arrival — never skipped, never rushed."],
            [<IconClipboard/>,"Full PPE Standards","All personnel meet PPE requirements. We verify compliance before incidents occur."],
            [<IconShield/>,"Licence Verification","All White Cards, HRWLs and trade certificates are verified with expiry tracking."],
            [<IconShield/>,"Ongoing Training","Safety refreshers, upskilling opportunities and career progression support."],
            [<IconShield/>,"Incident Response","Clear, fast incident reporting and investigation support — always communicated professionally."],
          ].map(([ic,t,d]) => (
            <Rev key={t} className="saf-card">
              <div className="saf-icon">{ic}</div>
              <h4>{t}</h4><p>{d}</p>
            </Rev>
          ))}
        </div>
      </div>
    </section>

    <section className="sec">
      <div className="wrap">
        <Rev style={{textAlign:"center",maxWidth:540,margin:"0 auto"}}>
          <span className="eyebrow">Client Feedback</span>
          <h2 style={{color:"var(--navy)"}}>What Our Clients Say</h2>
        </Rev>
        <TestimonialSlider />
      </div>
    </section>

    <div className="dual-cta">
      <div className="dual-panel panel-blue">
        <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=700&q=80" alt="Builder reviewing plans" loading="lazy"/>
        <div className="dual-pc">
          <span className="tag">Employers &amp; Contractors</span>
          <h3>Need Workers?<br/>We've Got You Covered.</h3>
          <p>Tell us your requirements and we'll get the right crew confirmed.</p>
          <button className="btn btn-gold" onClick={() => goTo("builders")}>How It Works for Employers</button>
        </div>
      </div>
      <div className="dual-panel panel-mid">
        <img src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=700&q=80" alt="Worker in PPE" loading="lazy"/>
        <div className="dual-pc">
          <span className="tag">Talent &amp; Tradespeople</span>
          <h3>Looking for<br/>Consistent Work?</h3>
          <p>Register with Delta Talent and access quality placements that match your skills.</p>
          <button className="btn btn-outline" onClick={() => goTo("workers")}>Find Work Today</button>
        </div>
      </div>
    </div>

    <section className="sec sec-ofw" id="request">
      <div className="wrap">
        <div className="req-inner">
          <Rev>
            <span className="eyebrow">Request Talent</span>
            <h2 style={{color:"var(--navy)",marginBottom:14}}>Ready to Strengthen Your Crew?</h2>
            <p className="lead" style={{marginBottom:30}}>Tell us what you need and our team will be in touch to discuss a solution that fits your project requirements.</p>
            {["Our team will review your request promptly","We'll work to match your schedule and requirements","All workers vetted and compliant","No lock-in contracts required"].map(t => (
              <div className="trust-pt" key={t}><IconCheckShield/><span>{t}</span></div>
            ))}
          </Rev>
          <Rev>
            <div className="form-card">
              <h3>Request Talent</h3>
              <p className="form-sub">Complete the form below and we'll be in touch shortly.</p>
              <RequestTalentForm />
            </div>
          </Rev>
        </div>
      </div>
    </section>

    <div className="cta-strip">
      <div className="wrap">
        <div className="cta-inner">
          <div className="cta-text"><h2>Ready to Work with Delta Talent?</h2><p>Let's talk about how we can support your next project.</p></div>
          <div className="cta-actions"><button className="btn btn-navy" onClick={() => goTo("contact")}>Contact Us</button></div>
        </div>
      </div>
    </div>
  </div>
);

const AboutPage = ({ goTo }) => (
  <div id="about">
    <div className="inner-hero"><div className="wrap">
      <span className="eyebrow">About Delta Talent</span>
      <h1>The Right People. <em>Ready to Work.</em></h1>
      <p className="lead">Delta Talent was founded on a simple belief: the construction industry deserves a more reliable and professional approach to labour hire.</p>
    </div></div>
    <section className="sec"><div className="wrap"><div className="about-split">
      <Rev className="img-block"><img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=900&q=80" alt="Delta Talent team" loading="lazy"/></Rev>
      <Rev>
        <span className="eyebrow">Our Story</span>
        <h2 style={{color:"var(--navy)"}}>Built from Experience. Focused on People.</h2>
        {[
          "With experience spanning construction, site management and recruitment, we have seen the challenges that arise when the right people are not in the right roles. From the pressure of keeping a project moving on site to the importance of finding dependable workers who understand what is expected of them, we have experienced the industry from multiple perspectives.",
          "That experience shaped the way Delta Talent was created.",
          "Rather than treating labour hire as simply filling a vacancy, Delta Talent focuses on understanding the requirements of each project, the expectations of each client and the qualities needed from every worker.",
          "The goal is straightforward: to connect construction businesses with reliable people who are ready to work, while creating genuine opportunities for workers looking to build their careers.",
          "At Delta Talent, we believe a successful workforce is built on more than availability. It is built on communication, accountability, reliability and strong relationships.",
          "Whether you are a builder looking for dependable workforce support, or a skilled worker looking for your next opportunity, Delta Talent is here to provide a professional, responsive and people-focused service."
        ].map((t,i) => <p key={t.slice(0,32)} style={{marginTop: i===0?16:12}}>{t}</p>)}
      </Rev>
    </div></div></section>
    <section className="sec sec-ofw"><div className="wrap">
      <Rev style={{textAlign:"center",maxWidth:520,margin:"0 auto 52px"}}>
        <span className="eyebrow">Our Values</span>
        <h2 style={{color:"var(--navy)"}}>What We Stand For</h2>
      </Rev>
      <div className="val-grid">
        {[
          ["01","Reliability","We do what we say we're going to do. Workers arrive on time, equipped and ready. If something changes, you hear from us first."],
          ["02","Quality Over Quantity","We'd rather send you eight excellent workers than twelve average ones. Every placement is a reflection of our reputation."],
          ["03","Safety First","No margin, no deadline, no budget justifies cutting corners on safety. It's not a policy — it's a value we genuinely hold."],
          ["04","Respect for Workers","The people we place are professionals. We treat them that way — with fair pay, safe placements and real opportunities to grow."],
          ["05","Long-Term Relationships","We're not interested in one-off transactions. Our goal is to become an extension of your team for the long haul."],
          ["06","Accountability","If something isn't right, we own it and fix it. No blame-shifting, no excuses. That's what it means to be a real partner."],
        ].map(([n,t,d]) => (
          <Rev key={n} className="val-card"><div className="val-num">{n}</div><h4>{t}</h4><p>{d}</p></Rev>
        ))}
      </div>
    </div></section>
    <div className="cta-strip"><div className="wrap"><div className="cta-inner">
      <div className="cta-text"><h2>Let's Build Something Together.</h2><p>Reach out to discuss your next project's workforce requirements.</p></div>
      <button className="btn btn-navy" onClick={() => goTo("contact")}>Get in Touch</button>
    </div></div></div>
  </div>
);

const BuildersPage = ({ goTo }) => (
  <div id="builders">
    <div className="inner-hero"><div className="wrap">
      <span className="eyebrow">Employers &amp; Contractors</span>
      <h1>The Workforce Gap <em>Stops Here.</em></h1>
      <p className="lead">When your project needs reliable, qualified workers, Delta Talent delivers. No surprises. No shortcuts. No excuses.</p>
      <div style={{marginTop:36,display:"flex",gap:14,flexWrap:"wrap"}}>
        <button className="btn btn-gold" onClick={() => goTo("home","request")}>Request Talent Now</button>
        <button className="btn btn-outline" onClick={() => goTo("contact")}>Talk to Our Team</button>
      </div>
    </div></div>
    <section className="sec"><div className="wrap">
      <Rev style={{maxWidth:600,marginBottom:52}}><span className="eyebrow">What We Supply</span><h2 style={{color:"var(--navy)"}}>Every Trade You Need. Whenever You Need It.</h2></Rev>
      <div className="svc-grid">
        {[
          [<IconUsers/>,"General Labourers","Site clearance, concrete works, landscaping, demolition, loading — ready to get stuck in."],
          [<IconTool/>,"Carpenters","Formwork, framing, fit-out and finishing — trade-qualified for residential and commercial projects."],
          [<IconClock/>,"Concreters & Steel Fixers","Slabs, footings, pours, reinforcement — experienced and delivering to spec."],
          [<IconUsers/>,"Machine Operators","HRWL-licensed excavator, forklift, EWP and heavy plant operators."],
          [<IconClipboard/>,"Site Supervisors","Experienced supervisors who take the reins on crew management and schedule adherence."],
          [<IconShield/>,"Scaffolders","Licensed scaffolders who work safely and efficiently at any height."],
        ].map(([ic,t,d]) => (
          <Rev key={t} className="svc-card"><div className="svc-icon">{ic}</div><h3>{t}</h3><p>{d}</p></Rev>
        ))}
      </div>
    </div></section>
    <section className="sec sec-ofw"><div className="wrap">
      <Rev style={{textAlign:"center",maxWidth:520,margin:"0 auto 52px"}}><span className="eyebrow">How It Works</span><h2 style={{color:"var(--navy)"}}>Getting Workers on Site is Simple</h2></Rev>
      <div className="process">
        {[
          ["1","Submit Your Request","Tell us your trade requirements, numbers, start date and project details via our form or by phone."],
          ["2","We Match & Confirm","We confirm the workers we're sending — names, licences and experience — as soon as we have the right people lined up."],
          ["3","Workers Are Inducted","Workers receive your site-specific induction before arrival — arriving prepared, not as strangers."],
          ["4","You Focus on Building","We handle timesheets, payroll, compliance and performance — leaving you free to run your project."],
        ].map(([n,t,d]) => (
          <Rev key={n} className="proc-step"><div className="proc-num">{n}</div><h4>{t}</h4><p>{d}</p></Rev>
        ))}
      </div>
    </div></section>
    <div className="cta-strip"><div className="wrap"><div className="cta-inner">
      <div className="cta-text"><h2>Need Workers on Site?</h2><p>Submit a request and we'll get your crew confirmed.</p></div>
      <button className="btn btn-navy" onClick={() => goTo("home","request")}>Request Talent Now</button>
    </div></div></div>
  </div>
);

const WorkersPage = ({ goTo }) => (
  <div id="workers">
    <div className="inner-hero"><div className="wrap">
      <span className="eyebrow">Talent &amp; Tradespeople</span>
      <h1>Your Skills Deserve <em>the Right Opportunity.</em></h1>
      <p className="lead">At Delta Talent, we work with skilled construction workers and tradespeople who want consistent work, fair pay and a team that actually has their back. If that sounds like you, we'd love to hear from you.</p>
      <div style={{marginTop:36}}><button className="btn btn-gold" onClick={() => goTo("contact")}>Get in Touch</button></div>
    </div></div>
    <section className="sec"><div className="wrap"><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:72,alignItems:"center"}} className="workers-split">
      <Rev><img src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80" alt="Tradespeople in PPE" loading="lazy" style={{borderRadius:12,width:"100%",aspectRatio:"4/3",objectFit:"cover"}}/></Rev>
      <Rev>
        <span className="eyebrow">Why Work with Delta Talent</span>
        <h2 style={{color:"var(--navy)"}}>We're in Your Corner</h2>
        <p style={{marginBottom:24, marginTop:16}}>We know what it's like to show up, do the hard work and not feel valued for it. Delta Talent was built to change that. We take the time to understand what you're looking for, place you in roles that suit your skills, and make sure you're treated with the respect you've earned.</p>
        <div className="check-list">
          {[
            ["Steady, Reliable Work","We focus on keeping you working consistently — not just placing you once and moving on."],
            ["Competitive Pay","You'll be paid fairly for your trade and your experience — no low-ball rates, no surprises."],
            ["Sites You Can Trust","We only send you to worksites that meet proper safety standards. Your safety matters — full stop."],
            ["Work That Suits You","Whether you want short-term jobs or something long-term, we'll find placements that fit your schedule and goals."],
            ["A Real Person to Call","When you need something sorted, you'll speak to someone who knows who you are — not a call centre."],
          ].map(([t,d]) => (
            <div key={t} className="check-item"><div className="check-icon"><IconCheck/></div><div><strong>{t}</strong><span>{d}</span></div></div>
          ))}
        </div>
      </Rev>
    </div></div></section>
    <section className="sec sec-ofw"><div className="wrap">
      <Rev style={{textAlign:"center",maxWidth:520,margin:"0 auto 52px"}}>
        <span className="eyebrow">How It Works</span>
        <h2 style={{color:"var(--navy)"}}>Getting Started is Simple</h2>
        <p style={{marginTop:14}}>We keep the process straightforward so you can focus on what you do best — getting on site and getting the job done.</p>
      </Rev>
      <div className="process">
        {[
          ["1","Reach Out","Give us a call or send a message through the contact page. Tell us your trade, your experience and what kind of work you're after."],
          ["2","Have a Chat","We'll have a straightforward conversation to get to know you — what you're good at, what you're looking for and how we can help."],
          ["3","Get Matched","We'll match you with roles that suit your skills and availability, and make sure you're set up and ready before you step on site."],
          ["4","Get to Work","Start earning. We'll stay in touch to make sure everything is going well and keep finding you the right opportunities as they come up."],
        ].map(([n,t,d]) => (
          <Rev key={n} className="proc-step"><div className="proc-num">{n}</div><h4>{t}</h4><p>{d}</p></Rev>
        ))}
      </div>
    </div></section>
    <div className="cta-strip"><div className="wrap"><div className="cta-inner">
      <div className="cta-text"><h2>Ready to Find Your Next Role?</h2><p>Get in touch today and let's find the right fit for you.</p></div>
      <button className="btn btn-navy" onClick={() => goTo("contact")}>Get in Touch</button>
    </div></div></div>
  </div>
);

const ServicesPage = ({ goTo }) => (
  <div id="services">
    <div className="inner-hero"><div className="wrap">
      <span className="eyebrow">Industries We Serve</span>
      <h1>The Right People, <em>Across the Right Industries.</em></h1>
      <p className="lead">Delta Talent supplies reliable, work-ready people across construction and related industries. Whether you need one person or an entire crew, we match the right talent to the right role — every time.</p>
    </div></div>
    <section className="sec"><div className="wrap">
      <Rev className="svc-intro">
        <span className="eyebrow">What We Cover</span>
        <h2 style={{color:"var(--navy)"}}>6 Industries. One Workforce Partner.</h2>
        <p style={{marginTop:16}}>From commercial construction and civil projects to plant operations, trades and facilities management — Delta Talent has the people and the experience to support your workforce needs across every stage of a project.</p>
      </Rev>
      <div className="ind-full-grid">
        {[
          ["01","Construction","Commercial, residential and mixed-use builds. Labourers, carpenters, concreters, formworkers and more — site-ready and compliant.","https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80"],
          ["02","Infrastructure","Large-scale infrastructure projects including rail, airports, utilities and major public works across NSW.","https://images.unsplash.com/photo-1581094651181-35942459ef62?w=800&q=80"],
          ["03","Civil","Roads, bridges, drainage, earthworks and civil construction. Experienced civil labourers and plant operators ready to go.","https://images.unsplash.com/photo-1503708928676-1cb796a0891e?w=800&q=80"],
          ["04","Plant & Equipment","Licensed machine operators for excavators, EWPs, forklifts, cranes and heavy plant across construction, civil and industrial sites.","https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80"],
          ["05","Facilities & Maintenance","Maintenance workers, cleaners, trades assistants and general facilities staff to keep operations running smoothly.","https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80"],
          ["06","Trades & Skilled Labour","Electricians, plumbers, painters, tilers and specialist tradespeople — licensed, experienced and available when you need them.","https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800&q=80"],
        ].map(([n,t,d,src]) => (
          <Rev key={n} className="ind-full-card">
            <img src={src} alt={t} loading="lazy"/><div className="ind-ov"></div>
            <div className="ind-label"><span className="ind-tag">{n}</span><h3>{t}</h3><p>{d}</p></div>
          </Rev>
        ))}
      </div>
    </div></section>
    <div className="cta-strip"><div className="wrap"><div className="cta-inner">
      <div className="cta-text"><h2>Don't See Your Industry?</h2><p>Get in touch and we'll let you know how we can help.</p></div>
      <div className="cta-actions">
        <button className="btn btn-navy" onClick={() => goTo("contact")}>Contact Us</button>
        <button className="btn btn-navy" onClick={() => goTo("home","request")}>Request Talent</button>
      </div>
    </div></div></div>
  </div>
);

const ContactPage = ({ goTo }) => {
  const cvSectionRef = useRef(null);
  return (
    <div id="contact">
      <div className="inner-hero"><div className="wrap">
        <span className="eyebrow">Get in Touch</span>
        <h1>We're Here for <em>Builders &amp; Workers.</em></h1>
        <p className="lead">Whether you're a business looking to strengthen your workforce, or a tradesperson ready for your next opportunity — we'd love to hear from you.</p>
      </div></div>
      <section className="sec sec-ofw"><div className="wrap">
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24,marginBottom:72}} className="contact-dual">
          <Rev style={{background:"var(--navy)",borderRadius:"var(--rl)",padding:44,display:"flex",flexDirection:"column",gap:20}}>
            <div style={{width:52,height:52,background:"rgba(232,160,32,.15)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--gold)"}}><IconClipboardList/></div>
            <div>
              <span className="eyebrow">Employers &amp; Businesses</span>
              <h3 style={{color:"var(--white)",marginBottom:10}}>Need reliable workers?</h3>
              <p style={{color:"rgba(255,255,255,.72)",fontSize:15}}>Tell us about your project and workforce requirements. We'll work with you to find the right people for the job — quickly and professionally.</p>
            </div>
            <ul style={{display:"flex",flexDirection:"column",gap:8,marginTop:4}}>
              {["Submit a Request Talent form","Call us to discuss your needs","Send us a message below"].map(t => (
                <li key={t} style={{display:"flex",alignItems:"center",gap:10,fontSize:14,color:"rgba(255,255,255,.65)"}}><span style={{color:"var(--gold)"}}><IconCheckShield size={16}/></span>{t}</li>
              ))}
            </ul>
            <button className="btn btn-gold" style={{alignSelf:"flex-start",marginTop:8}} onClick={() => goTo("home","request")}>Request Talent</button>
          </Rev>
          <Rev style={{background:"var(--white)",border:"2px solid var(--grey-light)",borderRadius:"var(--rl)",padding:44,display:"flex",flexDirection:"column",gap:20}}>
            <div style={{width:52,height:52,background:"var(--grey-light)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--navy)"}}><IconUsers size={26}/></div>
            <div>
              <span className="eyebrow">Talent &amp; Tradespeople</span>
              <h3 style={{color:"var(--navy)",marginBottom:10}}>Looking for your next role?</h3>
              <p style={{fontSize:15}}>Get in touch or submit your CV below. We'll have a chat about what you're after and work to find you consistent, quality work that suits your skills and availability.</p>
            </div>
            <ul style={{display:"flex",flexDirection:"column",gap:8,marginTop:4}}>
              {["Submit your CV using the form below","Call us for a quick conversation","Drop us a message — no pressure"].map(t => (
                <li key={t} style={{display:"flex",alignItems:"center",gap:10,fontSize:14,color:"var(--text-light)"}}><span style={{color:"var(--gold-dark)"}}><IconCheckShield size={16}/></span>{t}</li>
              ))}
            </ul>
            <button className="btn btn-navy" style={{alignSelf:"flex-start",marginTop:8}} onClick={() => cvSectionRef.current?.scrollIntoView({behavior:"smooth"})}>Submit Your CV</button>
          </Rev>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:20,marginBottom:72}} className="contact-info-row">
          <Rev style={{background:"var(--white)",border:"1px solid var(--grey-light)",borderRadius:"var(--rl)",padding:"28px 24px",display:"flex",alignItems:"center",gap:16}}>
            <div className="ci-icon"><IconMail/></div>
            <div><strong style={{display:"block",fontSize:13,color:"var(--text)",marginBottom:3}}>Email</strong><a href="mailto:enquiries@deltatalent.com.au" style={{fontSize:15,color:"var(--navy)",fontWeight:600}}>enquiries@deltatalent.com.au</a></div>
          </Rev>
          <Rev style={{background:"var(--white)",border:"1px solid var(--grey-light)",borderRadius:"var(--rl)",padding:"28px 24px",display:"flex",alignItems:"center",gap:16}}>
            <div className="ci-icon"><IconClock2/></div>
            <div><strong style={{display:"block",fontSize:13,color:"var(--text)",marginBottom:3}}>Hours</strong><span style={{fontSize:15,color:"var(--text-light)"}}>Mon–Fri, 7am–5pm AEST</span></div>
          </Rev>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24,alignItems:"start"}} className="contact-forms-row">
          <Rev className="cf-card-navy">
            <div style={{width:52,height:52,background:"rgba(232,160,32,.15)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--gold)",marginBottom:4}}><IconMail size={24}/></div>
            <div>
              <h3>Send Us a Message</h3>
              <p className="form-sub" style={{marginBottom:0}}>Builders, workers or anyone with a question &mdash; drop us a message and we&rsquo;ll get back to you.</p>
            </div>
            <MessageForm/>
          </Rev>
          <Rev className="cf-card-white" ref={cvSectionRef}>
            <div style={{width:52,height:52,background:"var(--grey-light)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--navy)",marginBottom:4}}><IconFile size={24}/></div>
            <div>
              <h3>Submit Your CV</h3>
              <p className="form-sub" style={{marginBottom:0}}>Looking for work? Tell us about yourself and attach your CV. We&rsquo;ll review it and reach out when we have something that suits you.</p>
            </div>
            <CVForm/>
          </Rev>
        </div>
      </div></section>
    </div>
  );
};

/* ---------- FOOTER ---------- */
const Footer = ({ goTo }) => (
  <footer className="footer">
    <div className="wrap">
      <div className="footer-top">
        <div className="footer-brand">
          <div style={{display:"flex",alignItems:"center",gap:11,cursor:"pointer"}} onClick={() => goTo("home")}>
            <div style={{width:30,height:30,background:"var(--gold)",clipPath:"polygon(50% 0%,100% 100%,0% 100%)",flexShrink:0}}></div>
            <span style={{fontFamily:"var(--fd)",fontSize:17,fontWeight:800,color:"var(--white)",letterSpacing:".06em",textTransform:"uppercase"}}>Delta Talent</span>
          </div>
          <p>Sydney's trusted construction workforce partner. Reliable people. Safer sites. Better outcomes.</p>
          <div className="footer-social">
            <a href="https://www.facebook.com/profile.php?id=61591030745187" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><IconFacebook/></a>
            <a href="https://www.instagram.com/DeltaTalentAU" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><IconInstagram/></a>
          </div>
        </div>
        <div className="footer-col"><h5>Company</h5><ul><li><span onClick={() => goTo("about")}>About Us</span></li><li><span onClick={() => goTo("contact")}>Contact</span></li></ul></div>
        <div className="footer-col"><h5>Services</h5><ul>
          <li><span onClick={() => goTo("services")}>Industries We Serve</span></li>
          <li><span onClick={() => goTo("builders")}>Employers</span></li>
          <li><span onClick={() => goTo("workers")}>Talent</span></li>
          <li><span onClick={() => goTo("home","request")}>Request Talent</span></li>
        </ul></div>
        <div className="footer-col"><h5>Contact</h5><ul>
          <li><a href="mailto:enquiries@deltatalent.com.au">enquiries@deltatalent.com.au</a></li>
          <li><span style={{fontSize:13,color:"rgba(255,255,255,.35)"}}>Mon–Fri, 7am–5pm AEST</span></li>
        </ul></div>
      </div>
      <div className="footer-bottom">
        <p>© {new Date().getFullYear()} Delta Talent Pty Ltd. All rights reserved. ABN 77 699 201 422</p>
        <div className="footer-legal"><a onClick={() => goTo("privacy")} style={{cursor:"pointer"}} data-testid="footer-privacy">Privacy Policy</a><a onClick={() => goTo("terms")} style={{cursor:"pointer"}} data-testid="footer-terms">Terms of Use</a></div>
      </div>
    </div>
  </footer>
);

/* ---------- MAIN SITE ---------- */
export default function Site() {
  const [page, setPage] = useState("home");
  const goTo = (pageId, scrollId) => {
    setPage(pageId);
    window.scrollTo(0, 0);
    if (scrollId) {
      setTimeout(() => {
        const el = document.getElementById(scrollId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };
  const pages = { home: HomePage, about: AboutPage, builders: BuildersPage, workers: WorkersPage, services: ServicesPage, contact: ContactPage, privacy: PrivacyPage, terms: TermsPage };
  const Current = pages[page] || HomePage;
  return (
    <>
      <Nav page={page} goTo={goTo} />
      <Current goTo={goTo} />
      <Footer goTo={goTo} />
    </>
  );
}
